import {
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Flex,
  Group,
  Input,
  Menu,
  ScrollArea,
  SegmentedControl,
  Switch,
  Table,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconCash,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconDownload,
  IconEye,
  IconInfoCircle,
  IconMail,
  IconPencil,
  IconPlus,
  IconRotate,
  IconSelector,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { daysAgoISODate, dateToEndOfDay, dateToStartOfDay, todayISODate } from '../../helpers/dateUtils';
import { invoiceStatusBadge, invoiceTypeLabel, invoiceTypeOf, sortInvoices } from '../../helpers/invoiceDisplay';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import MetricsSelector from '../MetricsSelector';
import ConfirmationModal from '../Modals/ConfirmationModal';
import Spinner from '../Spinner';
import InvoiceSendModal from './InvoiceSendModal';

const inactiveIntegrationLabel = (count) => `${count} ${count === 1 ? 'domain' : 'domains'} not integration-active`;

const numberSearchDebounceMs = 300;

// GET /api/admin/invoices/archive refuses more ids than this. Kept well under the
// API's own cap: the ids go in a query string on a plain navigation, and 500
// 24-char ObjectIds would build a ~12.6KB request line — over nginx's default
// 8KB request-line buffer, which would surface as an unexplained 414.
const archiveIdLimit = 250;

const archiveLocales = [
  { value: 'bg', label: 'BG' },
  { value: 'en', label: 'EN' },
  { value: 'all', label: 'Both' },
];

// Invoices issued before the API grew drafts carry no status, so an existing
// voidedAt decides and everything else reads as issued.
const statusOf = (invoice) => invoice.status ?? (invoice.voidedAt ? 'void' : 'issued');

const approveWarning =
  'Approving is irreversible: each draft is issued and permanently keeps its number. A proforma is numbered from its own counter.';
const deleteWarning = 'This draft has no number yet, so deleting it leaves no gap in the sequence.';
const voidWarning = 'Voiding is irreversible: the document keeps its number and can never be un-voided.';

const SortableTh = ({ sortKey, label, tooltip, sort, onSort, ...thProps }) => {
  const active = sort.key === sortKey;
  const Icon = active ? (sort.direction === 'asc' ? IconChevronUp : IconChevronDown) : IconSelector;
  const control = (
    <UnstyledButton onClick={() => onSort(sortKey)} aria-label={`Sort by ${label}`}>
      <Group gap={4} wrap="nowrap">
        <Text size="sm" fw={600}>
          {label}
        </Text>
        <Icon size={14} style={{ color: active ? undefined : 'var(--mantine-color-dimmed)' }} />
      </Group>
    </UnstyledButton>
  );

  return (
    <Table.Th aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'} {...thProps}>
      {tooltip ? (
        <Tooltip label={tooltip} position="top">
          {control}
        </Tooltip>
      ) : (
        control
      )}
    </Table.Th>
  );
};

const SuperAdminInvoices = () => {
  const initialPeriodDays = 30;
  // maxDate is about a year in the future (for filtering)
  const maxDate = DateTime.now().plus({ days: 366 }).toISODate();

  const fetch = useFetch();
  const navigate = useNavigate();
  const [loadError, setLoadError] = useState();
  // Store ISO date strings (YYYY-MM-DD)
  const [startDate, setStartDate] = useState(daysAgoISODate(initialPeriodDays - 1));
  const [endDate, setEndDate] = useState(todayISODate());
  const [invoices, setInvoices] = useState();
  const [loading, setLoading] = useState(false);
  const [onlyInactiveIntegration, setOnlyInactiveIntegration] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [numberSearch, setNumberSearch] = useState('');
  const [debouncedNumberSearch] = useDebouncedValue(numberSearch, numberSearchDebounceMs);
  const [sort, setSort] = useState({ key: null, direction: 'asc' });
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirming, setConfirming] = useState();
  const [sendTarget, setSendTarget] = useState();

  // Number-search responses can arrive out of order; only the latest request may write state.
  const loadSeq = useRef(0);

  const loadInvoices = useCallback(() => {
    const seq = ++loadSeq.current;
    setLoadError(undefined);
    setInvoices(undefined);
    setSelectedIds([]);
    // Convert ISO date strings to full timestamps for the API. A number search
    // sends no range at all, which is how the API is told to search every date.
    const startTs = encodeURIComponent(dateToStartOfDay(startDate, 'local'));
    const endTs = encodeURIComponent(dateToEndOfDay(endDate, 'local'));
    const query = debouncedNumberSearch
      ? `number=${encodeURIComponent(debouncedNumberSearch)}`
      : `from=${startTs}&to=${endTs}`;
    fetch(`/api/admin/invoices?${query}`)
      .then(({ invoices }) => {
        if (seq === loadSeq.current) setInvoices(invoices);
      })
      .catch((error) => {
        if (seq === loadSeq.current) setLoadError(error);
      });
  }, [startDate, endDate, debouncedNumberSearch, fetch]);

  useEffect(loadInvoices, [loadInvoices]);

  // Actions outlive the render that started them; their reload must use the current filters, not the captured ones.
  const loadInvoicesRef = useRef(loadInvoices);
  loadInvoicesRef.current = loadInvoices;
  const reloadInvoices = useCallback(() => loadInvoicesRef.current(), []);

  // Older invoices predate the field, so a missing count reads as zero.
  const filteredInvoices = invoices?.filter(
    (invoice) =>
      (!onlyInactiveIntegration || invoice.inactiveIntegrationDomains > 0) &&
      (statusFilter === 'all' || statusOf(invoice) === statusFilter) &&
      (typeFilter === 'all' || invoiceTypeOf(invoice) === typeFilter)
  );
  const visibleInvoices = sortInvoices(filteredInvoices, sort);
  const visibleIds = visibleInvoices?.map(({ id }) => id) ?? [];
  const selectedVisibleIds = visibleIds.filter((id) => selectedIds.includes(id));
  // Approval numbers drafts in request order, so the ids keep the API's order rather than the displayed sort.
  const selectedDraftIds =
    filteredInvoices
      ?.filter((invoice) => statusOf(invoice) === 'draft' && selectedIds.includes(invoice.id))
      .map(({ id }) => id) ?? [];
  const searchingByNumber = debouncedNumberSearch !== '';
  const tooManyToArchive = selectedVisibleIds.length > archiveIdLimit;

  const onSort = (key) =>
    setSort((current) =>
      current.key === key ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: 'asc' }
    );

  const notifyError = (error, fallback) =>
    notifications.show({ title: 'Error', message: error.message || fallback, color: 'danger' });

  const save = (id, update) => {
    setLoading(true);
    return fetch(`/api/admin/invoices/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ invoice: update }),
    })
      .then(
        () => {
          notifications.show({
            title: 'Invoice saved',
            message: 'Your changes have been saved successfully.',
            color: 'success',
          });
          reloadInvoices();
        },
        (error) => {
          notifyError(error, 'There was an error saving your changes.');
          // A refusal means the row is stale.
          reloadInvoices();
        }
      )
      .finally(() => setLoading(false));
  };

  const invoiceAction = (id, path, method, notify) => {
    setLoading(true);
    return fetch(`/api/admin/invoices/${id}${path}`, { method })
      .then(
        // A second then-argument, not .catch: a throw from notify() (an unexpected
        // response shape) must not be reported as the request having failed.
        (response) => {
          reloadInvoices();
          notifications.show({ color: 'success', ...notify(response ?? {}) });
        },
        (error) => {
          notifyError(error, 'The invoice could not be updated.');
          // A refusal means the row is stale.
          reloadInvoices();
        }
      )
      .finally(() => setLoading(false));
  };

  const approveSelected = () => {
    const ids = selectedDraftIds;
    setLoading(true);
    return fetch('/api/admin/invoices/approve', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    })
      .then(
        ({ approved = [], failed = [] } = {}) => {
          // notifications.show must run before reloadInvoices: the failure list below
          // reads `invoices`, the pre-reload array, to label ids no longer in the list.
          notifications.show({
            title: `${approved.length} of ${ids.length} drafts approved`,
            message: failed.length ? (
              // The notification root clips at 200px without scrolling, so the list scrolls inside a smaller box.
              <ScrollArea.Autosize mah={110}>
                {failed.map(({ id, error }) => {
                  const failedInvoice = invoices.find((candidate) => candidate.id === id);
                  const label = failedInvoice
                    ? `${failedInvoice.account.name} (${new Date(failedInvoice.day).toLocaleDateString()})`
                    : `Invoice ${id}`;
                  return <Text key={id} size="sm">{`${label}: ${error}`}</Text>;
                })}
              </ScrollArea.Autosize>
            ) : (
              'Every approved draft now has a permanent number from its own counter.'
            ),
            color: failed.length ? 'danger' : 'success',
            // The reasons exist nowhere else once the list reloads, so they stay until dismissed.
            autoClose: failed.length ? false : undefined,
          });
          reloadInvoices();
        },
        (error) => {
          notifyError(error, 'The drafts could not be approved.');
          // A refusal means the list (and the ticked checkboxes) is stale.
          reloadInvoices();
        }
      )
      .finally(() => setLoading(false));
  };

  return (
    <>
      <ConfirmationModal
        show={!!confirming}
        message={confirming?.message}
        close={() => setConfirming(undefined)}
        loading={loading}
        confirm={(done) => confirming.run().finally(done)}
      />
      {sendTarget && (
        <InvoiceSendModal invoice={sendTarget} onClose={() => setSendTarget(undefined)} onSent={reloadInvoices} />
      )}
      <Card withBorder>
        <Card.Section inheritPadding py="sm">
          <Flex gap="md" align="flex-end" justify="space-between" wrap="wrap">
            <Flex gap="md" align="flex-end" wrap="wrap">
              <TextInput
                w={180}
                label="Invoice number"
                placeholder="e.g. 10423"
                inputMode="numeric"
                value={numberSearch}
                onChange={(event) => setNumberSearch(event.currentTarget.value.replace(/\D/g, ''))}
              />
              <Input.Wrapper label="Status" labelElement="div">
                <SegmentedControl
                  size="sm"
                  aria-label="Status"
                  value={statusFilter}
                  disabled={!invoices}
                  onChange={setStatusFilter}
                  data={[
                    { value: 'all', label: 'All' },
                    { value: 'draft', label: 'Draft' },
                    { value: 'issued', label: 'Issued' },
                    { value: 'void', label: 'Void' },
                  ]}
                />
              </Input.Wrapper>
              <Input.Wrapper label="Type" labelElement="div">
                <SegmentedControl
                  size="sm"
                  aria-label="Type"
                  value={typeFilter}
                  disabled={!invoices}
                  onChange={setTypeFilter}
                  data={[
                    { value: 'all', label: 'All types' },
                    { value: 'invoice', label: 'Invoices' },
                    { value: 'proforma', label: 'Proformas' },
                  ]}
                />
              </Input.Wrapper>
              <Input.Wrapper label="Date range" labelElement="div">
                <Box inert={searchingByNumber} style={searchingByNumber ? { opacity: 0.4 } : undefined}>
                  <MetricsSelector
                    startDate={startDate}
                    endDate={endDate}
                    maxDate={maxDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    rowSize="12"
                  />
                </Box>
              </Input.Wrapper>
              <Switch
                label="Only invoices with domains that are not integration-active"
                checked={onlyInactiveIntegration}
                disabled={!invoices}
                onChange={(event) => setOnlyInactiveIntegration(event.currentTarget.checked)}
              />
            </Flex>
            <Button size="sm" onClick={() => navigate('/superAdmin/invoices/new')}>
              <IconPlus size={14} /> New Invoice
            </Button>
          </Flex>
          {searchingByNumber && (
            <Text size="xs" c="dimmed" mt="xs">
              Searching by number looks at every date, so the date range is ignored.
            </Text>
          )}
          <Group my="sm">
            <Button
              size="sm"
              disabled={loading || selectedDraftIds.length === 0}
              onClick={() => setConfirming({ message: approveWarning, run: approveSelected })}
            >
              <IconCheck size={14} /> Approve selected ({selectedDraftIds.length})
            </Button>
            <Tooltip
              label={`At most ${archiveIdLimit} invoices can be zipped at once.`}
              disabled={!tooManyToArchive}
              position="top"
            >
              {/* The tooltip sits on the wrapper because a disabled button fires no mouse events. */}
              <Box>
                <Menu>
                  <Menu.Target>
                    <Button size="sm" variant="default" disabled={selectedVisibleIds.length === 0 || tooManyToArchive}>
                      <IconDownload size={14} /> Download selected ({selectedVisibleIds.length})
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {archiveLocales.map(({ value, label }) => (
                      <Menu.Item
                        key={value}
                        component="a"
                        href={`/api/admin/invoices/archive?ids=${selectedVisibleIds.join(',')}&locale=${value}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <IconDownload size={14} /> {label}
                      </Menu.Item>
                    ))}
                  </Menu.Dropdown>
                </Menu>
              </Box>
            </Tooltip>
          </Group>
          {loading && <Spinner />}

          {visibleInvoices ? (
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>
                    <Checkbox
                      aria-label="Select all visible invoices"
                      disabled={visibleIds.length === 0}
                      checked={visibleIds.length > 0 && selectedVisibleIds.length === visibleIds.length}
                      indeterminate={selectedVisibleIds.length > 0 && selectedVisibleIds.length < visibleIds.length}
                      onChange={(event) => {
                        const { checked } = event.currentTarget;
                        setSelectedIds((ids) =>
                          checked ? [...new Set([...ids, ...visibleIds])] : ids.filter((id) => !visibleIds.includes(id))
                        );
                      }}
                    />
                  </Table.Th>
                  <SortableTh sortKey="number" label="Number" sort={sort} onSort={onSort} visibleFrom="md" />
                  <Table.Th>Status</Table.Th>
                  <SortableTh sortKey="day" label="Day" sort={sort} onSort={onSort} />
                  <Table.Th>Account</Table.Th>
                  <Table.Th>Period</Table.Th>
                  <SortableTh
                    sortKey="amount"
                    label="Total (Excl. VAT)"
                    tooltip="Sorted by the EUR equivalent"
                    sort={sort}
                    onSort={onSort}
                  />
                  <Table.Th>Sent</Table.Th>
                  <Table.Th>Paid</Table.Th>
                  <Table.Th></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {visibleInvoices.map((invoice) => {
                  const status = statusOf(invoice);
                  return (
                    <Table.Tr key={invoice.id} style={{ textDecoration: status === 'void' ? 'line-through' : null }}>
                      <Table.Td>
                        <Checkbox
                          aria-label={`Select ${invoice.number || 'draft'} for ${invoice.account.name} (${new Date(invoice.day).toLocaleDateString()})`}
                          checked={selectedIds.includes(invoice.id)}
                          onChange={(event) => {
                            const { checked } = event.currentTarget;
                            setSelectedIds((ids) =>
                              checked ? [...ids, invoice.id] : ids.filter((id) => id !== invoice.id)
                            );
                          }}
                        />
                      </Table.Td>
                      <Table.Td visibleFrom="md">
                        <Group gap="xs">
                          <strong>{invoice.number || '—'}</strong>
                          {invoiceTypeOf(invoice) === 'proforma' && (
                            <Badge color="violet" variant="light">
                              Proforma
                            </Badge>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={invoiceStatusBadge(status).color} variant="light">
                          {invoiceStatusBadge(status).label}
                        </Badge>
                      </Table.Td>
                      <Table.Td>{new Date(invoice.day).toLocaleDateString()}</Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <Text span>{invoice.account.name}</Text>
                          {invoice.inactiveIntegrationDomains > 0 && (
                            <Badge color="orange" variant="light">
                              {inactiveIntegrationLabel(invoice.inactiveIntegrationDomains)}
                            </Badge>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        {invoice.billingPeriod &&
                          new Date(invoice.billingPeriod.start).toLocaleDateString() +
                            ' - ' +
                            new Date(invoice.billingPeriod.end).toLocaleDateString()}
                      </Table.Td>
                      <Table.Td>
                        {Intl.NumberFormat(Navigator.language, {
                          style: 'currency',
                          currency: invoice.currency,
                        }).format(invoice.totalBeforeTax)}
                      </Table.Td>
                      <Table.Td>
                        {invoice.sentAt && (
                          <>
                            {new Date(invoice.sentAt).toLocaleDateString()}{' '}
                            <Tooltip label={`Sent to: ${invoice.sentTo.join(', ')}`} position="top">
                              <IconInfoCircle size={14} />
                            </Tooltip>
                          </>
                        )}{' '}
                      </Table.Td>

                      <Table.Td>
                        {invoice.paidAt ? (
                          <>
                            {new Date(invoice.paidAt).toLocaleDateString()}
                            <br />
                            <Text c="dimmed" component="span">
                              {invoice.stripePaymentIntentId ? 'Card' : 'Manual'}
                            </Text>
                          </>
                        ) : (
                          ''
                        )}
                      </Table.Td>
                      <Table.Td ta="right">
                        <Menu>
                          <Menu.Target>
                            <Button size="sm" variant="default">
                              Actions
                            </Button>
                          </Menu.Target>
                          <Menu.Dropdown>
                            {status === 'draft' && (
                              <>
                                <Menu.Item
                                  disabled={loading}
                                  onClick={() =>
                                    setConfirming({
                                      message: approveWarning,
                                      run: () =>
                                        invoiceAction(invoice.id, '/approve', 'POST', ({ invoice: approved } = {}) => ({
                                          title: 'Invoice approved',
                                          message: `The ${invoiceTypeLabel(approved?.type).toLowerCase()} has been issued as # ${approved?.number}.`,
                                        })),
                                    })
                                  }
                                >
                                  <IconCheck size={14} /> Approve
                                </Menu.Item>
                                <Menu.Item component="a" href={`#/superAdmin/invoices/${invoice.id}`}>
                                  <IconPencil size={14} /> Edit
                                </Menu.Item>
                                <Menu.Item
                                  disabled={loading}
                                  onClick={() =>
                                    invoiceAction(invoice.id, '/regenerate', 'POST', ({ invoice: regenerated }) => ({
                                      title: 'Draft regenerated',
                                      message: regenerated
                                        ? 'The draft has been replaced with a freshly generated one.'
                                        : 'The account had nothing to charge for that day, so the draft was removed.',
                                    }))
                                  }
                                >
                                  <IconRotate size={14} /> Regenerate
                                </Menu.Item>
                                <Menu.Item
                                  disabled={loading}
                                  onClick={() =>
                                    setConfirming({
                                      message: deleteWarning,
                                      run: () =>
                                        invoiceAction(invoice.id, '', 'DELETE', () => ({
                                          title: 'Draft deleted',
                                          message: 'The draft invoice has been deleted.',
                                        })),
                                    })
                                  }
                                >
                                  <IconTrash size={14} /> Delete
                                </Menu.Item>
                              </>
                            )}
                            {status === 'issued' && (
                              <>
                                <Menu.Item disabled={loading} onClick={() => setSendTarget(invoice)}>
                                  <IconMail size={14} /> Send
                                </Menu.Item>
                                {invoice.paidAt ? (
                                  <Menu.Item
                                    disabled={loading}
                                    onClick={() => {
                                      save(invoice.id, { paidAt: null });
                                    }}
                                  >
                                    <IconX size={14} /> Mark NOT paid
                                  </Menu.Item>
                                ) : (
                                  <Menu.Item
                                    disabled={loading}
                                    onClick={() => {
                                      save(invoice.id, { paidAt: new Date() });
                                    }}
                                  >
                                    <IconCash size={14} /> Mark paid (and collect if possible)
                                  </Menu.Item>
                                )}
                                <Menu.Item
                                  disabled={loading}
                                  onClick={() =>
                                    setConfirming({
                                      message: voidWarning,
                                      run: () =>
                                        invoiceAction(invoice.id, '/void', 'POST', () => ({
                                          title: 'Invoice voided',
                                          message: 'The invoice keeps its number but no longer counts as payable.',
                                        })),
                                    })
                                  }
                                >
                                  <IconX size={14} /> Void
                                </Menu.Item>
                              </>
                            )}
                            {/* The details page opens an invoice without a status in the editor, not read-only. */}
                            {invoice.status && invoice.status !== 'draft' && (
                              <Menu.Item component="a" href={`#/superAdmin/invoices/${invoice.id}`}>
                                <IconEye size={14} /> View
                              </Menu.Item>
                            )}
                            <Menu.Item
                              component="a"
                              href={`/api/admin/invoices/${invoice.id}/bg`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <IconDownload size={14} /> Download BG Version
                            </Menu.Item>

                            <Menu.Item
                              component="a"
                              href={`/api/admin/invoices/${invoice.id}/en`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <IconDownload size={14} /> Download EN Version
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          ) : loadError ? (
            <ErrorMessage message={loadError.message} />
          ) : (
            <Spinner />
          )}
        </Card.Section>
      </Card>
    </>
  );
};

export default SuperAdminInvoices;
